import { useState, useCallback, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import type { ColorGradingConfig, ColumnColorGrading, ColumnVisibility, ColorGradingThreshold } from "../datagrid-configs/types";
import "./ColorGradingModal.css";

/**
 * Default thresholds for percentage columns.
 * These represent a traffic light system: red (low) -> yellow (medium) -> green (high)
 */
const DEFAULT_THRESHOLDS: ColorGradingThreshold[] = [
	{ threshold: 0, color: "#fee2e2" },      // Red (0-49%)
	{ threshold: 0.5, color: "#fef9c3" },    // Yellow (50-74%)
	{ threshold: 0.75, color: "#dcfce7" },   // Green (75-100%)
];

/** Min/max allowed for a threshold at the given index so ordering stays strict (below < above). */
function getThresholdMinMax(
	thresholds: ColorGradingThreshold[],
	index: number
): { minPercent: number; maxPercent: number } {
	const sortedIndices = thresholds
		.map((_, i) => i)
		.sort((a, b) => thresholds[a].threshold - thresholds[b].threshold);
	const position = sortedIndices.indexOf(index);
	const prevPercent =
		position === 0 ? -1 : Math.round(thresholds[sortedIndices[position - 1]].threshold * 100);
	const nextPercent =
		position === sortedIndices.length - 1
			? 101
			: Math.round(thresholds[sortedIndices[position + 1]].threshold * 100);
	return {
		minPercent: position === 0 ? 0 : prevPercent + 1,
		maxPercent: position === sortedIndices.length - 1 ? 100 : nextPercent - 1,
	};
}

interface PercentageColumn {
	key: string;
	label: string;
	group: string;
}

interface ColorGradingModalProps {
	/** List of percentage columns that can have color grading */
	percentageColumns: PercentageColumn[];
	/** Current color grading configuration */
	colorGradingConfig: ColorGradingConfig;
	/** Callback when configuration changes */
	onConfigChange: (config: ColorGradingConfig) => void;
	/** Callback to close the modal */
	onClose: () => void;
	/** When set, only show color grading for columns that are visible (hide hidden columns) */
	columnVisibility?: ColumnVisibility;
}

/**
 * ColorGradingModal - Modal for configuring color grading on percentage columns.
 * Allows users to enable/disable color grading per column and customize thresholds.
 */
export function ColorGradingModal({
	percentageColumns,
	colorGradingConfig,
	onConfigChange,
	onClose,
	columnVisibility,
}: ColorGradingModalProps) {
	// Only show color grading for visible columns when columnVisibility is provided
	const visiblePercentageColumns = useMemo(() => {
		if (!columnVisibility) return percentageColumns;
		return percentageColumns.filter((col) => columnVisibility[col.key] !== false);
	}, [percentageColumns, columnVisibility]);

	// Local state for editing (includes all columns so hidden columns keep their config)
	const [localConfig, setLocalConfig] = useState<ColorGradingConfig>(() => {
		const config: ColorGradingConfig = {};
		percentageColumns.forEach((col) => {
			config[col.key] = colorGradingConfig[col.key] || {
				enabled: false,
				thresholds: [...DEFAULT_THRESHOLDS],
			};
		});
		return config;
	});

	// Track which column is expanded for threshold editing
	const [expandedColumn, setExpandedColumn] = useState<string | null>(null);

	// Group columns by their group name (only visible columns for display)
	const columnsByGroup = useMemo(() => {
		const groups: Record<string, PercentageColumn[]> = {};
		visiblePercentageColumns.forEach((col) => {
			if (!groups[col.group]) {
				groups[col.group] = [];
			}
			groups[col.group].push(col);
		});
		return groups;
	}, [visiblePercentageColumns]);

	// Toggle color grading for a column
	const handleToggle = useCallback((columnKey: string) => {
		setLocalConfig((prev) => ({
			...prev,
			[columnKey]: {
				...prev[columnKey],
				enabled: !prev[columnKey].enabled,
			},
		}));
	}, []);

	// Update a specific threshold. Empty/invalid input is ignored so backspace doesn't set 0.
	// Values are clamped to the allowed range so a threshold cannot go above the next or below the previous.
	const handleThresholdChange = useCallback(
		(columnKey: string, index: number, field: "threshold" | "color", value: number | string) => {
			setLocalConfig((prev) => {
				if (field === "threshold") {
					const num = Number(value);
					if (value === "" || Number.isNaN(num)) {
						return prev;
					}
					const { minPercent, maxPercent } = getThresholdMinMax(prev[columnKey].thresholds, index);
					const clamped = Math.min(Math.max(num, minPercent), maxPercent);
					const normalized = clamped / 100;
					const newThresholds = [...prev[columnKey].thresholds];
					newThresholds[index] = {
						...newThresholds[index],
						threshold: normalized,
					};
					return {
						...prev,
						[columnKey]: {
							...prev[columnKey],
							thresholds: newThresholds,
						},
					};
				}
				const newThresholds = [...prev[columnKey].thresholds];
				newThresholds[index] = {
					...newThresholds[index],
					color: value as string,
				};
				return {
					...prev,
					[columnKey]: {
						...prev[columnKey],
						thresholds: newThresholds,
					},
				};
			});
		},
		[]
	);

	// Add a new threshold
	const handleAddThreshold = useCallback((columnKey: string) => {
		setLocalConfig((prev) => {
			const existingThresholds = prev[columnKey].thresholds;
			// Find a gap to add new threshold
			let newThreshold = 0.5;
			if (existingThresholds.length > 0) {
				const lastThreshold = existingThresholds[existingThresholds.length - 1].threshold;
				newThreshold = Math.min(lastThreshold + 0.1, 0.99);
			}
			const newThresholds = [
				...existingThresholds,
				{ threshold: newThreshold, color: "#e0e0e0" },
			].sort((a, b) => a.threshold - b.threshold);
			return {
				...prev,
				[columnKey]: {
					...prev[columnKey],
					thresholds: newThresholds,
				},
			};
		});
	}, []);

	// Remove a threshold
	const handleRemoveThreshold = useCallback((columnKey: string, index: number) => {
		setLocalConfig((prev) => {
			const newThresholds = prev[columnKey].thresholds.filter((_, i) => i !== index);
			return {
				...prev,
				[columnKey]: {
					...prev[columnKey],
					thresholds: newThresholds.length > 0 ? newThresholds : [...DEFAULT_THRESHOLDS],
				},
			};
		});
	}, []);

	// Reset a column to default thresholds
	const handleResetColumn = useCallback((columnKey: string) => {
		setLocalConfig((prev) => ({
			...prev,
			[columnKey]: {
				...prev[columnKey],
				thresholds: [...DEFAULT_THRESHOLDS],
			},
		}));
	}, []);

	// Apply to all columns in the same group
	const handleApplyToGroup = useCallback((sourceColumnKey: string, groupName: string) => {
		setLocalConfig((prev) => {
			const sourceConfig = prev[sourceColumnKey];
			const newConfig = { ...prev };
			percentageColumns
				.filter((col) => col.group === groupName && col.key !== sourceColumnKey)
				.forEach((col) => {
					newConfig[col.key] = {
						enabled: sourceConfig.enabled,
						thresholds: sourceConfig.thresholds.map((t) => ({ ...t })),
					};
				});
			return newConfig;
		});
	}, [percentageColumns]);

	const visibleColumnKeys = useMemo(
		() => new Set(visiblePercentageColumns.map((col) => col.key)),
		[visiblePercentageColumns]
	);

	// Enable/disable all columns (when columnVisibility is set, only visible columns)
	const handleEnableAll = useCallback(() => {
		setLocalConfig((prev) => {
			const newConfig: ColorGradingConfig = {};
			Object.keys(prev).forEach((key) => {
				const shouldEnable = visibleColumnKeys.size === 0 || visibleColumnKeys.has(key);
				newConfig[key] = { ...prev[key], enabled: shouldEnable };
			});
			return newConfig;
		});
	}, [visibleColumnKeys]);

	const handleDisableAll = useCallback(() => {
		setLocalConfig((prev) => {
			const newConfig: ColorGradingConfig = {};
			Object.keys(prev).forEach((key) => {
				const shouldDisable = visibleColumnKeys.size === 0 || visibleColumnKeys.has(key);
				newConfig[key] = { ...prev[key], enabled: !shouldDisable ? prev[key].enabled : false };
			});
			return newConfig;
		});
	}, [visibleColumnKeys]);

	// Count enabled columns (only visible when columnVisibility is set)
	const enabledCount = useMemo(() => {
		return Object.entries(localConfig).filter(
			([key, c]) => (visibleColumnKeys.size === 0 || visibleColumnKeys.has(key)) && c.enabled
		).length;
	}, [localConfig, visibleColumnKeys]);

	// Confirm handler - sort thresholds before saving so persisted config is sorted
	const handleConfirm = useCallback(() => {
		const sortedConfig: ColorGradingConfig = {};
		Object.keys(localConfig).forEach((key) => {
			const col = localConfig[key];
			sortedConfig[key] = {
				...col,
				thresholds: [...col.thresholds].sort((a, b) => a.threshold - b.threshold),
			};
		});
		onConfigChange(sortedConfig);
		onClose();
	}, [localConfig, onConfigChange, onClose]);

	// Prevent body scroll when modal is open
	useEffect(() => {
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "";
		};
	}, []);

	// Handle backdrop click
	const handleBackdropClick = useCallback(
		(e: React.MouseEvent) => {
			if (e.target === e.currentTarget) {
				onClose();
			}
		},
		[onClose]
	);

	// Handle escape key
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};
		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [onClose]);

	// Render threshold preview bar
	const renderThresholdPreview = (config: ColumnColorGrading) => {
		if (config.thresholds.length === 0) return null;
		
		const sortedThresholds = [...config.thresholds].sort((a, b) => a.threshold - b.threshold);
		
		return (
			<div className="colorGradingModal-preview">
				{sortedThresholds.map((t, i) => {
					const width = i < sortedThresholds.length - 1
						? (sortedThresholds[i + 1].threshold - t.threshold) * 100
						: (1 - t.threshold) * 100;
					return (
						<div
							key={i}
							className="colorGradingModal-previewSegment"
							style={{
								backgroundColor: t.color,
								width: `${width}%`,
							}}
							title={`${(t.threshold * 100).toFixed(0)}% - ${i < sortedThresholds.length - 1 ? (sortedThresholds[i + 1].threshold * 100).toFixed(0) : 100}%`}
						/>
					);
				})}
			</div>
		);
	};

	return createPortal(
		<div className="colorGradingModal-overlay" onClick={handleBackdropClick}>
			<div className="colorGradingModal">
				{/* Header */}
				<div className="colorGradingModal-header">
					<h2 className="colorGradingModal-title">Color Grading Settings</h2>
					<button
						type="button"
						className="colorGradingModal-closeBtn"
						onClick={onClose}
						aria-label="Close"
					>
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none">
							<path
								d="M1 1L13 13M1 13L13 1"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
							/>
						</svg>
					</button>
				</div>

				{/* Content */}
				<div className="colorGradingModal-content">
					{/* Quick Actions */}
					<div className="colorGradingModal-quickActions">
						<button
							type="button"
							className="colorGradingModal-actionBtn"
							onClick={handleEnableAll}
						>
							Enable All
						</button>
						<button
							type="button"
							className="colorGradingModal-actionBtn"
							onClick={handleDisableAll}
						>
							Disable All
						</button>
						<span className="colorGradingModal-count">
							{enabledCount} of {visiblePercentageColumns.length} enabled
						</span>
					</div>

					{/* Column List by Group */}
					{Object.entries(columnsByGroup).map(([groupName, columns]) => (
						<div key={groupName} className="colorGradingModal-group">
							<h3 className="colorGradingModal-groupTitle">{groupName}</h3>
							<div className="colorGradingModal-columnList">
								{columns.map((col) => {
									const config = localConfig[col.key];
									const isExpanded = expandedColumn === col.key;
									
									return (
										<div key={col.key} className="colorGradingModal-columnItem">
											<div className="colorGradingModal-columnRow">
												{/* Toggle */}
												<button
													type="button"
													className={`colorGradingModal-toggle ${config.enabled ? "colorGradingModal-toggle--on" : ""}`}
													onClick={() => handleToggle(col.key)}
													aria-label={`Toggle ${col.label}`}
												>
													<span className="colorGradingModal-toggleKnob" />
												</button>

												{/* Column Name */}
												<span className="colorGradingModal-columnName">{col.label}</span>

												{/* Preview (when enabled) */}
												{config.enabled && renderThresholdPreview(config)}

												{/* Expand/Collapse Button */}
												{config.enabled && (
													<button
														type="button"
														className={`colorGradingModal-expandBtn ${isExpanded ? "colorGradingModal-expandBtn--expanded" : ""}`}
														onClick={() => setExpandedColumn(isExpanded ? null : col.key)}
														aria-label={isExpanded ? "Collapse" : "Expand"}
													>
														<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
															<path
																d="M2 4L6 8L10 4"
																stroke="currentColor"
																strokeWidth="2"
																strokeLinecap="round"
																strokeLinejoin="round"
															/>
														</svg>
													</button>
												)}
											</div>

											{/* Expanded Threshold Editor - render in sorted order with min/max per row */}
											{isExpanded && config.enabled && (() => {
												const sortedWithIndex = config.thresholds
													.map((t, i) => ({ threshold: t, originalIndex: i }))
													.sort((a, b) => a.threshold.threshold - b.threshold.threshold);
												return (
												<div className="colorGradingModal-thresholdEditor">
													<div className="colorGradingModal-thresholdList">
														{sortedWithIndex.map(({ threshold, originalIndex }) => {
															const { minPercent, maxPercent } = getThresholdMinMax(
																config.thresholds,
																originalIndex
															);
															return (
															<div key={originalIndex} className="colorGradingModal-thresholdRow">
																<span className="colorGradingModal-thresholdLabel">
																	≥
																</span>
																<input
																	type="number"
																	className="colorGradingModal-thresholdInput"
																	value={Math.round(threshold.threshold * 100)}
																	min={minPercent}
																	max={maxPercent}
																	onChange={(e) =>
																		handleThresholdChange(col.key, originalIndex, "threshold", e.target.value)
																	}
																/>
																<span className="colorGradingModal-thresholdLabel">%</span>
																<input
																	type="color"
																	className="colorGradingModal-colorInput"
																	value={threshold.color}
																	onChange={(e) =>
																		handleThresholdChange(col.key, originalIndex, "color", e.target.value)
																	}
																/>
																{config.thresholds.length > 1 && (
																	<button
																		type="button"
																		className="colorGradingModal-removeBtn"
																		onClick={() => handleRemoveThreshold(col.key, originalIndex)}
																		aria-label="Remove threshold"
																	>
																		×
																	</button>
																)}
															</div>
															);
														})}
													</div>
													<div className="colorGradingModal-thresholdActions">
														<button
															type="button"
															className="colorGradingModal-smallBtn"
															onClick={() => handleAddThreshold(col.key)}
														>
															+ Add Threshold
														</button>
														<button
															type="button"
															className="colorGradingModal-smallBtn colorGradingModal-smallBtn--secondary"
															onClick={() => handleResetColumn(col.key)}
														>
															Reset to Default
														</button>
														<button
															type="button"
															className="colorGradingModal-smallBtn colorGradingModal-smallBtn--secondary"
															onClick={() => handleApplyToGroup(col.key, col.group)}
														>
															Apply to Group
														</button>
													</div>
												</div>
												);
											})()}
										</div>
									);
								})}
							</div>
						</div>
					))}
				</div>

				{/* Footer */}
				<div className="colorGradingModal-footer">
					<button
						type="button"
						className="colorGradingModal-btn colorGradingModal-btn--cancel"
						onClick={onClose}
					>
						Cancel
					</button>
					<button
						type="button"
						className="colorGradingModal-btn colorGradingModal-btn--confirm"
						onClick={handleConfirm}
					>
						Apply
					</button>
				</div>
			</div>
		</div>,
		document.body
	);
}

export default ColorGradingModal;

